import React, { useEffect, useState } from 'react';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Dialog from 'src/components/common/Dialog';
import PersonnelToast from '../../components/PersonnelToast';
import useFetch from 'use-http';
import PersonnelDocument from 'src/models/personnel/PersonnelDocument';
import moment from 'moment';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { useForm } from 'react-hook-form';
import PersonnelDocumentDto from 'src/models/personnel/PersonnelDocumentDto';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import TSDILandman from 'src/models/personnel/TSDILandman';

interface IPersonnelPaperworkDialogProps {
    category: string;
    categoryId: number;
    type: string; // insurance, paperwork
    closePanel: () => void;
    dialogName: string;
    selectedPersonnel: TSDILandman;
    refresh: () => void;
}

const PersonnelPaperworkDialog = (props: IPersonnelPaperworkDialogProps) => {
    const { register, reset, getValues } = useForm();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [personnelDocuments, setPersonnelDocuments] = useState<Array<PersonnelDocument>>();
    const { get, put, delete: remove, response } = useFetch();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setPersonnelDocuments(null);
        if (props.type === 'insurance') {
            fetchInsuranceDocuments();
        } else if (props.type === 'paperwork') {
            fetchPaperworkDocuments();
        }
    }, [props.category, props.type]);

    useEffect(() => {
        reset({ document: personnelDocuments });
    }, [personnelDocuments]);

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const closePanel = () => {
        setPersonnelDocuments(undefined);
        setToast(null);
        props.closePanel();
    };

    const fetchInsuranceDocuments = async () => {
        setIsLoading(true);

        if (props.selectedPersonnel === undefined) {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
            setPersonnelDocuments(undefined);
            setIsLoading(false);
            return;
        }

        if (props.selectedPersonnel.lmNo === null || props.selectedPersonnel.lmNo <= 0) {
            showToast('Invalid landman number attached to personnel', `rgba(255,0,0,0.5)`);
            setPersonnelDocuments(undefined);
            setIsLoading(false);
            return;
        }

        let result = await get(`/v1.0/landmen/personnel/${props.selectedPersonnel?.lmNo}/${props.categoryId}/get-insurance-category-documents`);

        if (response.ok) {
            if (result) {
                result?.forEach((document: PersonnelDocument) => {
                    document.expDt = document.expDt ? moment(document.expDt).format('YYYY-MM-DD') : '';
                    document.fileDate = document.fileDate ? moment(document.fileDate).format('YYYY-MM-DD') : '';
                });
            }
            setPersonnelDocuments(result);
            setIsLoading(false);
        } else {
            showToast('Unable to retrieve documents: ' + response, `rgba(255,0,0,0.5)`);
            setPersonnelDocuments(undefined);
            setIsLoading(false);
        }
    };

    const fetchPaperworkDocuments = async () => {
        setIsLoading(true);

        if (props.selectedPersonnel === undefined) {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
            setPersonnelDocuments(undefined);
            setIsLoading(false);
            return;
        }
        // if there is not a record in the LM table for the employee (highly unlikely, but possible)
        if (props.selectedPersonnel.lmNo === null || props.selectedPersonnel.lmNo <= 0) {
            showToast('Invalid landman number attached to personnel', `rgba(255,0,0,0.5)`);
            setPersonnelDocuments(undefined);
            setIsLoading(false);
            return;
        }

        let result = await get(`/v1.0/landmen/personnel/${props.selectedPersonnel?.lmNo}/${props.categoryId}/get-paperwork-category-documents`);

        if (response.ok) {
            if (result) {
                result?.forEach((document: PersonnelDocument) => {
                    document.compDate = document.compDate ? moment(document.compDate).format('YYYY-MM-DD') : '';
                    document.fileDate = document.fileDate ? moment(document.fileDate).format('YYYY-MM-DD') : '';
                });
            }
            setPersonnelDocuments(result);
            setIsLoading(false);
        } else {
            showToast('Unable to retrieve documents: ' + response, `rgba(255,0,0,0.5)`);
            setPersonnelDocuments(undefined);
            setIsLoading(false);
        }
    };

    const downloadFile = async (doc: PersonnelDocument, index: number) => {
        setIsLoading(true);

        // getting file from s3 if originally uploaded to s3
        if (doc.s3Id !== null && doc.s3Id !== undefined && doc.s3Id > 0) {
            await get(`/v1.0/s3Bucket/by-id/${doc.s3Id}`);

            if (response.ok) {
                const contentDisposition = response.headers.get('Content-Disposition');
                let downloadFileName = '';

                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (fileNameMatch && fileNameMatch[1]) {
                        downloadFileName = fileNameMatch[1];
                    }
                }

                // downloading
                await response.blob().then((blob) => {
                    const blobUrl = window.URL.createObjectURL(blob);
                    const a = document.getElementById(`a_${index}`) as HTMLAnchorElement;
                    if (a) {
                        a.href = blobUrl;
                        a.download = downloadFileName;
                        a.click();
                        window.URL.revokeObjectURL(blobUrl);
                    }
                    showToast('Download successful!');
                    setIsLoading(false);
                });
            } else {
                setIsLoading(false);
                showToast('Failed to fetch file: ' + response.data, `rgba(255,0,0,0.5)`);
            }
            // else if blob is stored in db
        } else if (doc.docFile !== null && doc.docFile !== undefined) {
            const binaryString = atob(doc.docFile);
            const byteNumbers = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                byteNumbers[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([byteNumbers], { type: `application/${doc.fileType}` });
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.getElementById(`a_${index}`) as HTMLAnchorElement;
            if (a) {
                a.href = blobUrl;
                a.download = doc.fileOrig;
                a.click();
                window.URL.revokeObjectURL(blobUrl);
            }

            showToast('Download successful!');
            setIsLoading(false);
        } else {
            showToast('Unable to find download data', `rgba(255,0,0,0.5)`);
        }
    };

    const updateFile = async (document: PersonnelDocument, index: number) => {
        setIsLoading(true);
        let existingData = getValues();

        if (props.type === 'insurance') {
            let data: PersonnelDocumentDto = new PersonnelDocumentDto();
            data.date = existingData.document[index].expDt;
            data.docId = document.docId;
            data.lmNo = document.lmNo;
            data.note = existingData.document[index].docNote;
            data.s3Id = document.s3Id;

            await put(`/v1.0/landmen/personnel/update-insurance-document`, data);

            if (response.ok) {
                showToast('Document saved!');
                setIsLoading(false);
            } else {
                setIsLoading(false);
                showToast('Unable to save document: ' + response, `rgba(255,0,0,0.5)`);
            }
        } else if (props.type === 'paperwork') {
            let data: PersonnelDocumentDto = new PersonnelDocumentDto();
            data.date = existingData.document[index].compDate;
            data.docId = document.docId;
            data.lmNo = document.lmNo;
            data.note = existingData.document[index].docNote;
            data.s3Id = document.s3Id;

            await put(`/v1.0/landmen/personnel/update-paperwork-document`, data);

            if (response.ok) {
                showToast('Document saved!');
                setIsLoading(false);
            } else {
                setIsLoading(false);
                showToast('Unable to save document: ' + response, `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Unknown error occured when submitting with type: ' + props.type, `rgba(255,0,0,0.5)`);
            setIsLoading(false);
            return;
        }

        props.refresh();
    };

    const deleteFile = async (document: PersonnelDocument) => {
        setIsLoading(true);

        if (props.type === 'insurance') {
            await remove(`/v1.0/landmen/personnel/${document.lmNo}/${document.s3Id}/${document.docId}/delete-insurance-document`);

            if (response.ok) {
                showToast('Document delete!');
                setIsLoading(false);
                await fetchInsuranceDocuments();
            } else {
                setIsLoading(false);
                showToast('Unable to delete document: ' + response, `rgba(255,0,0,0.5)`);
            }
        } else if (props.type === 'paperwork') {
            await remove(`/v1.0/landmen/personnel/${document.lmNo}/${document.s3Id}/${document.docId}/delete-paperwork-document`);

            if (response.ok) {
                showToast('Document deleted');
                await fetchPaperworkDocuments();
                setIsLoading(false);
            } else {
                showToast('Unable to delete document: ' + response, `rgba(255,0,0,0.5)`);
                setIsLoading(false);
            }
        } else {
            console.log('error?');
            return;
        }
    };

    return (
        <Dialog title={`Manage ${props.category} Documents`} dialogName={props.dialogName} bodyStyle={{ width: '75%', height: '65%' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                <table style={{ width: '100%' }} id="documentTable">
                    <thead>
                        <tr style={{ width: '100%' }}>
                            {/* Need? this would be for selecting document(s) to email - if needed, change note to 48%*/}
                            {/* <th style={{ width: '2%' }} /> */}
                            <th style={{ width: '5%' }}>Upload Date</th>
                            <th style={{ width: '25%' }}>Original File Name</th>
                            <th style={{ width: '5%' }}>File Size (kb)</th>
                            <th style={{ width: '50%' }}>Note</th>
                            <th style={{ width: '5%' }}>{props.type === 'insurance' ? 'Exp. Date' : 'Comp. Date'}</th>
                            <th style={{ width: '10%' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {personnelDocuments?.map((document: PersonnelDocument, index: number) => (
                            <tr style={{ width: '100%' }} key={index}>
                                {/* Need? this would be for selecting document(s) to email - if needed, change note to 48%*/}
                                {/* <td style={{ width: '2%', justifyContent: 'cetner' }}>
                            <input
                                name={`document.tranDate`}
                                type="checkbox"
                                style={{ width: '100%', display: 'block' }}
                            />
                        </td> */}
                                <td style={{ width: '5%' }}>
                                    <RHFInputBox
                                        register={register}
                                        name={`document.${index}.fileDate`}
                                        type="date"
                                        style={{ width: '100%', display: 'block' }}
                                        disabled={true}
                                        defaultVal={document?.fileDate?.toString()}
                                    />
                                </td>
                                <td style={{ width: '25%' }}>
                                    <RHFInputBox
                                        register={register}
                                        name={`document.${index}.fileOrig`}
                                        type="text"
                                        style={{ width: '100%', display: 'block' }}
                                        disabled={true}
                                        defaultVal={document?.fileOrig}
                                    />
                                </td>
                                <td style={{ width: '5%' }}>
                                    <RHFInputBox
                                        register={register}
                                        name={`document.${index}.fileSize`}
                                        type="text"
                                        style={{ width: '100%', display: 'block' }}
                                        disabled={true}
                                        defaultVal={document?.fileSize?.toString()}
                                    />
                                </td>
                                <td style={{ width: '50%' }}>
                                    <RHFInputBox
                                        register={register}
                                        name={`document.${index}.docNote`}
                                        type="text"
                                        style={{ width: '100%', display: 'block' }}
                                        defaultVal={document?.docNote}
                                    />
                                </td>
                                <td style={{ width: '5%' }}>
                                    <RHFInputBox
                                        register={register}
                                        name={props.type === 'insurance' ? `document.${index}.expDt` : `document.${index}.compDate`}
                                        type="date"
                                        style={{ width: '100%', display: 'block' }}
                                        defaultVal={props.type === 'insurance' ? document?.expDt?.toString() : document?.compDate?.toString()}
                                    />
                                </td>
                                <td style={{ width: '10%', verticalAlign: 'bottom', paddingLeft: '2%', justifyContent: 'center' }}>
                                    <div className="form-row" style={{ marginLeft: '2%' }}>
                                        <a onClick={() => updateFile(document, index)}>
                                            <i style={{ paddingRight: '1%', fontSize: '2.5rem' }} className="far fa-save mr-1 clickable" />
                                        </a>
                                        <a onClick={() => downloadFile(document, index)}>
                                            <i style={{ paddingRight: '1%', fontSize: '2.5rem' }} className="far fa-download mr-1 clickable" />
                                        </a>
                                        <a id={`a_${index}`} style={{ display: 'none' }} />
                                        <a onClick={() => deleteFile(document)}>
                                            <i style={{ fontSize: '2.5rem' }} className="far fa-trash mr-1 clickable" />
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {isLoading && (
                <div
                    style={{
                        top: 0,
                        left: 0,
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999,
                    }}
                >
                    <LoadingSpinner />
                </div>
            )}
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center', textAlign: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Close'}
                    onClick={closePanel}
                />
            </div>
        </Dialog>
    );
};

export default PersonnelPaperworkDialog;


// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/paperwork/PersonnelPaperworkDialog.tsx